import { FC, useContext } from 'react';

import { useMutation } from '@apollo/client';
import {
  Button,
  Modal,
  TextArea,
  NumberInput,
  ConsumptionIcon,
  IncomeIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import notificationService from 'services/notificationService';
import { SettingsContext } from 'settings';
import { TCashOrderType } from 'types';

import s from './CashOrderModal.module.scss';
import { FormValues, ICashOrderModalProps, IConfig } from './interfaces';
import { CREATE_CASH_ORDER } from './schema';

export const CashOrderModal: FC<ICashOrderModalProps> = ({
  type,
  onClose,
  className = '',
  style,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const CONFIGS_MAP: Record<TCashOrderType, IConfig> = {
    RKO: {
      title: t('orders.outcome'),
      Icon: ConsumptionIcon,
      iconColor: 'error',
      submitText: t('orders.outcome.submit'),
      successNotificationTitle: t('orders.outcome.created'),
    },
    PKO: {
      title: t('orders.income'),
      Icon: IncomeIcon,
      iconColor: 'green',
      submitText: t('orders.income.submit'),
      successNotificationTitle: t('orders.income.created'),
    },
  };

  const {
    data: { me },
  } = useContext(AuthContext);

  const [createCashOrder, { loading }] = useMutation(CREATE_CASH_ORDER, {
    onCompleted: () => {
      notificationService.info({
        t,
        type: 'success',
        title: CONFIGS_MAP[type].successNotificationTitle,
        dismiss: 3,
      });
      onClose();
    },
    onError: () => {
      notificationService.info({
        t,
        type: 'error',
        title: t('errors.cash_order'),
      });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormValues>({ mode: 'onChange' });

  const { title, Icon, iconColor, submitText } = CONFIGS_MAP[type];

  return (
    <Modal
      style={style}
      className={cn(s.container, className)}
      onClose={onClose}
    >
      <form
        className={s.form}
        onSubmit={handleSubmit(({ comment, sum }) => {
          if (!permissions?.has('cash_order_create')) return;

          createCashOrder({
            variables: { input: { type, sum, comment } },
          });
        })}
      >
        <header>
          <Icon size="m" color={iconColor} />
          {title}
        </header>
        <div className={s.operator}>
          {`${me?.last_name} ${me?.first_name} ${me?.middle_name}`}
        </div>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <NumberInput
              {...field}
              title={t('sum')}
              placeholder={'500'}
              rightContent={currency.sign}
              error={error?.message}
            />
          )}
          name="sum"
          control={control}
          defaultValue=""
          rules={{
            required: t('errors.required'),
            min: {
              value: 0,
              message: t('errors.min', 0, currency.sign),
            },
          }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <TextArea
              {...field}
              rows={2}
              title={t('comment')}
              placeholder=""
              error={error?.message}
            />
          )}
          name="comment"
          control={control}
          defaultValue=""
          rules={{
            maxLength: {
              value: 500,
              message: t('errors.maxLength', 500),
            },
          }}
        />
        <Button
          type="submit"
          fullWidth
          disabled={
            !isDirty || loading || !permissions?.has('cash_order_create')
          }
          className={s.button}
        >
          {submitText}
        </Button>
      </form>
    </Modal>
  );
};
